import { useEffect, useMemo, useState } from 'react';
import { Topbar } from '@/components/Topbar';
import { Card } from '@/components/Card';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { Select } from '@/components/Select';
import { Modal } from '@/components/Modal';
import { EmptyState } from '@/components/EmptyState';
import {
  adRulesService, AdRule, AdRuleMetric, AdRulePlatform, CreateAdRulePayload,
} from '@/services/ad-rules.service';
import { Zap, Plus, Trash2, PauseCircle, Bell, History, ShieldAlert } from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';

const METRIC_LABELS: Record<AdRuleMetric, string> = {
  spend: 'Spend',
  cpl: 'Cost per lead',
  cpc: 'Cost per click',
  ctr: 'CTR (%)',
  impressions: 'Impressions',
  clicks: 'Clicks',
  leads: 'Leads',
};

const PLATFORM_LABELS: Record<AdRulePlatform, string> = { meta: 'Meta (FB + IG)', tiktok: 'TikTok' };

/** "Pause any Meta campaign whose Cost per lead exceeds 25 over the last 7 days." */
function sentence(p: Partial<CreateAdRulePayload>): string {
  if (!p.metric || p.threshold === undefined || !p.platform) return '';
  const verb = p.action === 'pause' ? 'Pause' : 'Flag';
  const cmp = p.operator === 'gt' ? 'exceeds' : 'drops below';
  return `${verb} any ${PLATFORM_LABELS[p.platform]} campaign whose ${METRIC_LABELS[p.metric].toLowerCase()} ${cmp} ${p.threshold} over the last ${p.lookbackDays ?? 7} day${(p.lookbackDays ?? 7) === 1 ? '' : 's'}.`;
}

export default function AdRules() {
  const [rules, setRules] = useState<AdRule[] | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [historyFor, setHistoryFor] = useState<AdRule | null>(null);
  const [busyUid, setBusyUid] = useState<string | null>(null);

  // Create form
  const [form, setForm] = useState<CreateAdRulePayload>({
    name: '',
    platform: 'meta',
    metric: 'cpl',
    operator: 'gt',
    threshold: 25,
    lookbackDays: 7,
    minSpend: 0,
    action: 'notify',
  });
  const [creating, setCreating] = useState(false);

  const load = async () => {
    try { setRules(await adRulesService.list()); }
    catch { setRules([]); /* interceptor surfaces */ }
  };
  useEffect(() => { load(); }, []);

  const preview = useMemo(() => sentence(form), [form]);

  const create = async () => {
    if (!form.name.trim()) { toast.error('Give the rule a name'); return; }
    if (!Number.isFinite(form.threshold)) { toast.error('Threshold must be a number'); return; }
    if (form.operator === 'lt' && !(form.minSpend && form.minSpend > 0)) {
      toast.error('"Drops below" rules need a minimum spend so new campaigns get a fair run');
      return;
    }
    setCreating(true);
    try {
      await adRulesService.create(form);
      toast.success('Rule created');
      setCreateOpen(false);
      setForm((f) => ({ ...f, name: '' }));
      await load();
    } catch (e: any) {
      // axios interceptor already toasted backend message
    } finally { setCreating(false); }
  };

  const toggleActive = async (rule: AdRule) => {
    setBusyUid(rule.ruleUid);
    try {
      await adRulesService.update(rule.ruleUid, { isActive: !rule.isActive });
      await load();
    } catch (e: any) {
      // axios interceptor already toasted backend message
    } finally { setBusyUid(null); }
  };

  const remove = async (rule: AdRule) => {
    if (!window.confirm(`Delete rule "${rule.name}"?`)) return;
    setBusyUid(rule.ruleUid);
    try {
      await adRulesService.remove(rule.ruleUid);
      toast.success('Rule deleted');
      await load();
    } catch (e: any) {
      // axios interceptor already toasted backend message
    } finally { setBusyUid(null); }
  };

  return (
    <>
      <Topbar
        title="Automated Rules"
        breadcrumb="Ads / Automated Rules"
        description="Watch your ad spend hourly and pause or flag campaigns that break your limits."
        right={<Button iconLeft={<Plus size={14} />} onClick={() => setCreateOpen(true)}>New rule</Button>}
      />

      <div className="w-full px-7 py-7">
        {rules === null ? (
          <div className="space-y-2">
            {[...Array(3)].map((_, i) => <div key={i} className="skeleton h-20 rounded-2xl" />)}
          </div>
        ) : rules.length === 0 ? (
          <EmptyState
            icon={<Zap size={40} className="text-text-muted" />}
            title="No automated rules yet"
            description={'Rules run hourly against your synced ad metrics. Example: "Pause any Meta campaign whose cost per lead exceeds 25 over the last 7 days."'}
            action={<Button iconLeft={<Plus size={14} />} onClick={() => setCreateOpen(true)}>Create your first rule</Button>}
          />
        ) : (
          <div className="space-y-3">
            {rules.map((rule) => (
              <Card key={rule.ruleUid} padded>
                <div className="flex flex-wrap items-center gap-3">
                  <div className={clsx(
                    'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl',
                    rule.action === 'pause' ? 'bg-danger-soft text-danger' : 'bg-warning-soft text-warning',
                  )}>
                    {rule.action === 'pause' ? <PauseCircle size={18} /> : <Bell size={18} />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate text-sm font-semibold text-text-primary">{rule.name}</span>
                      <span className="rounded-full bg-surface-alt px-2 py-0.5 text-2xs uppercase text-text-muted">
                        {PLATFORM_LABELS[rule.platform]}
                      </span>
                      {!rule.isActive && (
                        <span className="rounded-full bg-surface-alt px-2 py-0.5 text-2xs font-semibold uppercase text-text-muted">off</span>
                      )}
                    </div>
                    <p className="mt-0.5 text-xs text-text-muted">{sentence(rule)}</p>
                    <p className="mt-0.5 text-2xs text-text-muted">
                      {rule.lastRunAt ? `Last checked ${new Date(rule.lastRunAt).toLocaleString()}` : 'Not evaluated yet'}
                      {rule.lastTriggeredAt ? ` · last triggered ${new Date(rule.lastTriggeredAt).toLocaleString()}` : ''}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-1.5">
                    <Button size="sm" variant="secondary" iconLeft={<History size={12} />} onClick={() => setHistoryFor(rule)}>
                      History{rule.recentTriggers?.length ? ` (${rule.recentTriggers.length})` : ''}
                    </Button>
                    <Button size="sm" variant="secondary" onClick={() => toggleActive(rule)} disabled={busyUid === rule.ruleUid}>
                      {rule.isActive ? 'Disable' : 'Enable'}
                    </Button>
                    <button
                      onClick={() => remove(rule)}
                      disabled={busyUid === rule.ruleUid}
                      className="rounded-lg p-2 text-text-muted hover:bg-danger-soft hover:text-danger disabled:opacity-50"
                      title="Delete rule"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Create rule */}
      <Modal open={createOpen} onClose={() => setCreateOpen(false)} title="New automated rule" widthClass="max-w-xl">
        <div className="space-y-3">
          <Input placeholder="Rule name — e.g. CPL guard" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <div className="grid grid-cols-2 gap-2">
            <Select value={form.platform} onChange={(e) => setForm({ ...form, platform: e.target.value as AdRulePlatform })}>
              <option value="meta">Meta (FB + IG)</option>
              <option value="tiktok">TikTok</option>
            </Select>
            <Select value={form.action} onChange={(e) => setForm({ ...form, action: e.target.value as 'pause' | 'notify' })}>
              <option value="notify">Notify admins only</option>
              <option value="pause">Pause the campaign</option>
            </Select>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <Select value={form.metric} onChange={(e) => setForm({ ...form, metric: e.target.value as AdRuleMetric })}>
              {Object.entries(METRIC_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </Select>
            <Select value={form.operator} onChange={(e) => setForm({ ...form, operator: e.target.value as 'gt' | 'lt' })}>
              <option value="gt">exceeds</option>
              <option value="lt">drops below</option>
            </Select>
            <Input
              placeholder="Threshold"
              inputMode="decimal"
              value={String(form.threshold)}
              onChange={(e) => setForm({ ...form, threshold: parseFloat(e.target.value) || 0 })}
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Select value={String(form.lookbackDays)} onChange={(e) => setForm({ ...form, lookbackDays: parseInt(e.target.value, 10) })}>
              {[1, 3, 7, 14, 30].map((d) => <option key={d} value={d}>Last {d} day{d === 1 ? '' : 's'}</option>)}
            </Select>
            <Input
              placeholder="Min spend before firing (for 'drops below')"
              inputMode="decimal"
              value={form.minSpend ? String(form.minSpend) : ''}
              onChange={(e) => setForm({ ...form, minSpend: parseFloat(e.target.value) || 0 })}
            />
          </div>

          {preview && (
            <div className="flex items-start gap-2 rounded-lg border border-primary/30 bg-primary-soft/40 px-3 py-2.5 text-xs text-text-secondary">
              <ShieldAlert size={14} className="mt-0.5 shrink-0 text-primary" />
              <span><strong className="text-text-primary">In plain English:</strong> {preview}</span>
            </div>
          )}
          <p className="text-2xs text-text-muted">
            Rules read your synced ad metrics every hour — connect and sync the platform first.
            A campaign that triggers is not re-triggered for 24 hours.
          </p>

          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setCreateOpen(false)} disabled={creating}>Cancel</Button>
            <Button onClick={create} loading={creating} disabled={creating}>Create rule</Button>
          </div>
        </div>
      </Modal>

      {/* Trigger history */}
      <Modal open={!!historyFor} onClose={() => setHistoryFor(null)} title={`History — ${historyFor?.name ?? ''}`}>
        {!historyFor?.recentTriggers?.length ? (
          <p className="py-6 text-center text-sm text-text-muted">This rule hasn't triggered yet.</p>
        ) : (
          <div className="max-h-96 space-y-2 overflow-y-auto">
            {historyFor.recentTriggers.map((t, i) => (
              <div key={i} className="rounded-lg border border-border bg-surface-alt/40 px-3 py-2 text-xs">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono text-2xs text-text-muted">campaign {t.entityId}</span>
                  <span className="text-2xs text-text-muted">{new Date(t.at).toLocaleString()}</span>
                </div>
                <div className="mt-0.5 text-text-primary">
                  {METRIC_LABELS[historyFor.metric]} was {t.metricValue} → {t.action === 'pause' ? (t.ok ? 'paused' : 'pause FAILED') : 'admins notified'}
                </div>
                {t.error && <div className="mt-0.5 text-danger">{t.error}</div>}
              </div>
            ))}
          </div>
        )}
      </Modal>
    </>
  );
}
